import { decodeHTML } from 'entities';
import { URL } from 'url';
import { GfShouldNotHappenError } from '../errors';
import { MediaType } from '../media-types';
import { identifyType } from '../mime-atts/identifyType';
import { AttInfoLocalBuffer, AttInfoString, InlineResult } from '../mime-atts/types';
import { Strbuf, StringEncoder } from '../types';
import { preferredTypeFromType } from '../types-and-exts';
import { isDef, isStr, partition, trimQuotePair, trimQuotePairNoWs } from '../utils/common';
import { getUrlNoQsOrFragment } from '../utils/ext';
import * as rx from '../utils/regex';
import { difference } from '../utils/set';
import { simpleMagic } from '../utils/simpleMagic';
import { rxsHtmlAttrs, rxsHtmlAttrValueMaybeInQuotes } from './regex';
import { removeAttrs } from './removeAttrs';

const MSG_IGNORE_CONTENT_LOCATION_INLINES = !!process.env['MSG_IGNORE_CONTENT_LOCATION_INLINES'];

type Att1 = Pick<
	AttInfoLocalBuffer,
	'attType' | 'contentId' | 'contentLocation' | 'contentType' | 'fileName' | 'buf'
>;
type Att2 = Pick<
	AttInfoString,
	| 'attType'
	| 'contentId'
	| 'contentLocation'
	| 'contentType'
	| 'fileName'
	| 'str'
	| 'toBufEncoding'
>;

type Att = Att1 | Att2;

interface ParsedLoc {
	str?: string;
	parsed?: URL;
}

interface AttLocs {
	lower?: ParsedLoc;
	decoded?: ParsedLoc;
}

interface AttWithLocs<T extends Att> {
	att: T;
	locs: AttLocs;
}

function maybeNewURL(urlStr: string): URL | undefined {
	let url: URL | undefined;
	try {
		url = new URL(urlStr);
	} catch (err) {
		// noop
	}
	return url;
}

function decodeURI(uri: string): string {
	if (uri.startsWith('%%') || uri.endsWith('%%')) {
		// A fail placeholder URL, like %%view_email_url%%
		return uri;
	}

	try {
		return uri
			.split('/')
			.map(s => decodeURIComponent(s))
			.join('/');
	} catch (err) {
		return uri;
	}
}

function getUpdatedAttBuf(
	att: Att,
	updatedAttStr: string | undefined,
	encodeString: StringEncoder | undefined
): Buffer {
	let buf: Buffer;

	if (att.attType === 'string') {
		const attStr = updatedAttStr ?? att.str;
		buf = encodeString
			? encodeString(attStr, att.toBufEncoding)
			: Buffer.from(attStr, (att.toBufEncoding as any) ?? 'utf8');
	} else {
		buf = isDef(updatedAttStr) ? Buffer.from(updatedAttStr, 'binary') : att.buf;
	}

	return buf;
}

function normalizeCid(contentId: string): string {
	const id = contentId
		.trim()
		.replace(/^<+|>+$/g, '')
		.toLowerCase();
	return id;
}

function allHaveSameData(atts: readonly Att[]): boolean {
	let allSame = true;
	let strbuf: Strbuf;

	const length = atts.length;
	for (let i = 0; i < length; ++i) {
		const att = atts[i];
		const thisData = att.attType === 'string' ? att.str : att.buf;

		// Set the data buf on first one, otherwise compare
		if (i === 0) {
			strbuf = thisData;
		} else if (typeof strbuf! !== typeof thisData) {
			allSame = false;
			break;
		} else if (isStr(thisData)) {
			// Strings
			if (thisData !== strbuf!) {
				allSame = false;
				break;
			}
		} else {
			// Buffers
			if (thisData.compare(strbuf! as Buffer) !== 0) {
				allSame = false;
				break;
			}
		}
	}

	return allSame;
}

function joinUrls(absUrl: string, relUrl: string): string {
	// If relUrl is query-string only, just add without removing base name
	if (relUrl.startsWith('?')) {
		return absUrl + relUrl;
	}

	const absParts = absUrl.split('/');
	absParts.pop();

	const relParts = relUrl.split('/');

	while (relParts.length > 0 && (relParts[0] === '.' || relParts[0] === '..')) {
		if (relParts[0] === '..') {
			absParts.pop();
		}

		relParts.shift();
	}

	const combined = [...absParts, ...relParts].join('/');
	return combined;
}

function tryMatchUrls(
	decodedHtmlUrl: string,
	attLoc: ParsedLoc,
	currentContLocLower: string | undefined,
	rootContLocLower: string | undefined
): boolean {
	decodedHtmlUrl = decodedHtmlUrl.toLowerCase();

	if (decodedHtmlUrl === attLoc.str || 'mhtml:' + decodedHtmlUrl === attLoc.str) {
		return true;
	}

	if (decodedHtmlUrl.startsWith('//')) {
		if (attLoc.str?.startsWith('https://') && 'https:' + decodedHtmlUrl === attLoc.str) {
			return true;
		}

		if (attLoc.str?.startsWith('http://') && 'http:' + decodedHtmlUrl === attLoc.str) {
			return true;
		}
	}

	// Try the <HTML content-location>!<att content-location> schema
	if (currentContLocLower && currentContLocLower + '!' + attLoc.str === decodedHtmlUrl) {
		return true;
	}

	// Try the <HTML content-location>!<att content-location> schema
	if (rootContLocLower && rootContLocLower + '!' + attLoc.str === decodedHtmlUrl) {
		return true;
	}

	// Sometimes the main HTML part may have a Content-Location,
	// and the attachments will have an absolute location, while the HREF will be relative to the current att
	if (currentContLocLower) {
		if (joinUrls(currentContLocLower.toLowerCase(), decodedHtmlUrl) === attLoc.str) {
			return true;
		}
	}

	// Sometimes the main HTML part may have a Content-Location,
	// and the attachments will have an absolute location, while the HREF will be relative to the root page
	if (rootContLocLower) {
		if (joinUrls(rootContLocLower, decodedHtmlUrl) === attLoc.str) {
			return true;
		}
	}

	// The URL class will re-encode the URIs
	if (
		attLoc.parsed &&
		attLoc.parsed.protocol &&
		attLoc.parsed.host &&
		decodeURI(attLoc.parsed.pathname + attLoc.parsed.search) === decodedHtmlUrl
	) {
		return true;
	}

	return false;
}

/**
 * Given a URL, this function tries to match that URL to one of the attachments using
 * Content-Type and Content-Location.
 * @param rawUrl Encoded URL - may be
 * @param atts
 * @param currentContLocLower
 */
function matchUrlToAtt<T extends Att>(
	rawUrl: string,
	atts: AttWithLocs<T>[],
	currentContLocLower: string | undefined,
	rootContLocLower: string | undefined
): AttWithLocs<T>[] {
	// Quick check for data URL
	if (/^data:/i.test(rawUrl)) {
		return [];
	}

	const url3 = decodeHTML(rawUrl);
	const url4 = decodeURI(url3);

	let matchingAtts: AttWithLocs<T>[];

	if (/^cid:/i.test(url4)) {
		// CID matching... normalize the cid
		const uriCid = normalizeCid(url4.slice(4));

		matchingAtts = atts.filter(att => {
			const attCid = normalizeCid(att.att.contentId);
			return uriCid === attCid;
		});
	} else {
		// Content location matching
		matchingAtts = atts.filter(att => {
			if (!att.att.contentLocation || MSG_IGNORE_CONTENT_LOCATION_INLINES) {
				return false;
			}

			// Try everything with content loc as-is
			if (
				att.locs.lower &&
				tryMatchUrls(url4, att.locs.lower, currentContLocLower, rootContLocLower)
			) {
				return true;
			}

			// Maybe the content-loc is URL encoded... try with decoded
			if (
				att.locs.decoded &&
				tryMatchUrls(url4, att.locs.decoded, currentContLocLower, rootContLocLower)
			) {
				return true;
			}

			return false;
		});
	}

	if (!matchingAtts.length) {
		return [];
	}

	if (matchingAtts.length > 1 && !allHaveSameData(matchingAtts.map(o => o.att))) {
		console.log(`Matched [${url4}] to multiple, different attachments`);
		return [];
	}

	return matchingAtts;
}

const rxsCssUrlMaybeInQuotes = rx.or(`"[^"()\\s]+"`, `'[^'()\\s]+'`, `[^'"();\\s]+`);
const rxsCssUrlMatchInParens = `url *\\( *(${rxsCssUrlMaybeInQuotes}) *\\)`;
const rxsCssUrlMatchNotInParens = `(${rxsCssUrlMaybeInQuotes})`;
const rxsCssImport = rx.or(
	`@import +${rx.ncg(rx.or(rxsCssUrlMatchInParens, rxsCssUrlMatchNotInParens))}`,
	rxsCssUrlMatchInParens
);

// <v:imagedata is for VML, which is sometimes in the HTML... e.g.:
// <!--[if gte vml 1]><v:shapetype....<v:imagedata src=...
// Can't really inline these... https://stackoverflow.com/questions/16968356/vml-base64-image
const rxsSrcTags = `(?:<img|<frame|<iframe|<script|<input|<v:imagedata|<v:fill)`;
const rxsBackgroundTags = `(?:<body|<td|<table)`;
const rxsHtmlUrlMatch = String.raw`((?:${rxsSrcTags}${rxsHtmlAttrs}\s+src)|(?:${rxsBackgroundTags}${rxsHtmlAttrs}\s+background)|(?:<link${rxsHtmlAttrs}\s+href))\s*=\s*(${rxsHtmlAttrValueMaybeInQuotes})(${rxsHtmlAttrs})`;
const rxLinkRel = new RegExp(String.raw`\srel\s*=\s*(${rxsHtmlAttrValueMaybeInQuotes})`, 'i');

const rxsHtmlMatch = rx.or(rxsHtmlUrlMatch, rxsCssImport);

interface HtmlUrlInlineResult<T extends Att> {
	html: string;

	// Attachments that have been matched via a URL... not necesarilly inlined if `<v:imagedata` src or if explicitly
	// told not to inline yet by the callback
	matched: Set<AttWithLocs<T>>;

	// Attachments that have been inlined
	inlined: Set<AttWithLocs<T>>;
}

/**
 * rxsHtmlMatch has the following order
 * 0. HTML tag match: <img src; <iframe src; <body background; <link href
 * 1. HTML URL attr value: "..."; '...', ...
 * 2. @import match: url("..."); url('...'); url(...)
 * 3. @import match: url "..."; url '...'; url ...
 * 4. @import match: "..."; '...'; ...
 *
 * @param html
 * @param atts
 * @param currentContLocLower
 * @param cbWithMatchingAtts
 * @param cbGetUpdatedAttData
 */
function doHtmlUrlInline<T extends Att>(
	html: string,
	atts: AttWithLocs<T>[],
	currentContLocLower: string | undefined,
	rootContLocLower: string | undefined,
	// Can be false to stop the replacement
	cbWithMatchingAtts: (atts: AttWithLocs<T>[]) => void | false,
	cbGetUpdatedAttData: (att: AttWithLocs<T>) => string | undefined,
	encodeString: StringEncoder | undefined
): HtmlUrlInlineResult<T> {
	const rxWhole = new RegExp(rxsHtmlMatch, 'gi');

	const matched = new Set<AttWithLocs<T>>();
	const inlined = new Set<AttWithLocs<T>>();

	// Saves the replacement string for a particular URL...
	// Disabled for now because a URL may be used in multiple places... like a <body background="...">
	// and an <img src="...">, in which case the replacement string would differ.
	// const cache = new Map<string, string>();

	const html2 = html.replace(
		rxWhole,
		(
			match,
			htmlTagStart: string | undefined,
			htmlUrl: string | undefined,
			htmlTagRest: string | undefined,
			urlCss1: string | undefined,
			urlCss2: string | undefined,
			urlCss3: string | undefined
		) => {
			const urlMatch = trimQuotePair(htmlUrl ?? urlCss1 ?? urlCss2 ?? urlCss3 ?? '');

			if (!urlMatch) {
				return match;
			}

			/* RDJ: cache disabled
        const cacheHit = cache.get(urlMatch);
        if (isDef(cacheHit)) {
            return cacheHit;
        }

        const result: string = ((): string => {
        */

			const matchingAtts = matchUrlToAtt(
				urlMatch,
				atts,
				currentContLocLower,
				rootContLocLower
			);
			if (!matchingAtts.length) {
				return match;
			}

			// Add to the matched set
			for (const matchingAtt of matchingAtts) {
				matched.add(matchingAtt);
			}

			if (cbWithMatchingAtts) {
				const doReplace = cbWithMatchingAtts(matchingAtts);
				if (doReplace === false) {
					return match;
				}
			}

			const htmlTagStartLower = htmlTagStart?.toLowerCase();
			if (
				htmlTagStartLower?.startsWith('<v:imagedata') ||
				htmlTagStartLower?.startsWith('<v:fill')
			) {
				// Can't actually inline these
				// https://stackoverflow.com/questions/16968356/vml-base64-image
				return match;
			}

			// Count each matching as inlined at this point
			for (const matchingAtt of matchingAtts) {
				inlined.add(matchingAtt);
			}

			const first = matchingAtts[0];
			const updatedAttStr = cbGetUpdatedAttData && cbGetUpdatedAttData(first);
			const buf = getUpdatedAttBuf(first.att, updatedAttStr, encodeString);

			let contentType =
				preferredTypeFromType(first.att.contentType) ??
				first.att.contentType ??
				MediaType.OctetStream;

			// If content type came out to be octet-stream, try running the buffer through simpleMagic
			// to check if we can get a better type resolution
			if (contentType === MediaType.OctetStream) {
				const fromMagic = simpleMagic(buf);
				if (fromMagic) {
					console.log(`simpleMagic() used to determine media type: ${fromMagic}`);
					contentType = fromMagic;
				}
			}

			if (htmlTagStartLower?.startsWith('<script')) {
				// Final check of JS type... sometimes JS is mistyped as HTML
				if (contentType === MediaType.OctetStream || contentType === MediaType.Html) {
					console.log(`Assuming ${MediaType.Js} because of <script> tag`);
					contentType = MediaType.Js;

					// Set the type back in the dupe atts as well for any not-visibly attachment logic later
					for (const att of matchingAtts) {
						att.att.contentType = MediaType.Js;
					}
				} else if (contentType !== MediaType.Js) {
					console.dir({
						contentType,
						match,
						htmlTagLower: htmlTagStartLower,
						att: first.att,
					});
					throw new Error('js not js?');
				}
			} else if (htmlTagStartLower?.startsWith('<link')) {
				// Final check of CSS type
				const linkeRelMatch = rxLinkRel.exec(match);
				if (linkeRelMatch) {
					const rel = trimQuotePairNoWs(linkeRelMatch[1]).toLowerCase();
					if (rel === 'stylesheet') {
						// Final check of CSS type... sometimes CSS is mistyped as HTML
						if (
							contentType === MediaType.OctetStream ||
							contentType === MediaType.Html
						) {
							console.log(
								`Assuming ${MediaType.Css} because of <link rel="stylesheet"> tag`
							);
							contentType = MediaType.Css;
							// Set the type back in the att as well for any not-visibly attachment logic later
							for (const att of matchingAtts) {
								att.att.contentType = MediaType.Css;
							}
						} else if (contentType !== MediaType.Css) {
							console.dir({
								contentType,
								match,
								htmlTagLower: htmlTagStartLower,
								att: first.att,
							});
							throw new Error('Css not css?');
						}
					}
				}
			} else if (contentType === MediaType.OctetStream) {
				// Check for text only, e.g. some server error when requesting an image
				if (/^[\r\n\t -~]+$/.test(buf.toString('binary'))) {
					console.log('Inline ASCII attachment? Allowing...');
				} else {
					console.dir({
						contentType,
						match,
						htmlTagLower: htmlTagStartLower,
						att: first.att,
					});
					throw new Error('Inlining item with unknown media type');
				}
			}

			const dataUrl = `data:${contentType};base64,${buf.toString('base64')}`;

			if (htmlTagStart && htmlTagStartLower) {
				if (htmlTagStartLower.endsWith('src')) {
					// RDJ: Added check for img srcset
					// When we replace the src, but leave srcset alone, Chrome will not display the normal src
					// and instead try to fetch the srcset src, which normally is forbidden
					if (htmlTagStartLower.startsWith('<img')) {
						const tag = htmlTagStart.slice(0, 4);
						const preOtherAttrs = htmlTagStart.slice(4, htmlTagStart.length - 3);
						const preOtherAttrsFiltered = removeAttrs(preOtherAttrs, 'srcset');
						const postOtherAttrsFiltered = removeAttrs(htmlTagRest ?? '', 'srcset');

						return (
							tag +
							preOtherAttrsFiltered +
							`src="${dataUrl}"` +
							postOtherAttrsFiltered
						);
					} else {
						return (
							htmlTagStart.slice(0, htmlTagStart.length - 3) +
							`src="${dataUrl}"` +
							htmlTagRest
						);
					}
				} else if (htmlTagStartLower.endsWith('background')) {
					return (
						htmlTagStart.slice(0, htmlTagStart.length - 10) +
						`style="background-image:url('${dataUrl}');"` +
						htmlTagRest
					);
				} else if (htmlTagStartLower.endsWith('href')) {
					return (
						htmlTagStart.slice(0, htmlTagStart.length - 4) +
						`href="${dataUrl}"` +
						htmlTagRest
					);
				} else {
					throw new GfShouldNotHappenError('Count not match HTML tag regex');
				}
			} else if (urlCss1 || urlCss2) {
				return `@import url('${dataUrl}')`;
			} else {
				return `url('${dataUrl}')`;
			}

			/* RDJ: cache disabled
        })();
    
        cache.set(urlMatch, result);
        return result;
        */
		}
	);

	return {
		html: html2,
		matched,
		inlined,
	};
}

function inlineIntoCssAndHtmlAtts<T extends Att>(
	atts: AttWithLocs<T>[],
	rootContLocLower: string | undefined,
	encodeString: StringEncoder | undefined,
	verbose = false
): {
	inlinedAtts: Set<AttWithLocs<T>>;
	updatedAttData: Map<AttWithLocs<T>, string>;
} {
	// This holds the original content of any atts that may themselves have further inlining
	// E.g. CSS atts that have CSS imports, and HTML atts for use in iframes
	const dataStrsOfImportAtts = new Map<AttWithLocs<T>, string>();

	// This holds the final content of any CSS atts that have had CSS imports inlined
	const updatedAttData = new Map<AttWithLocs<T>, string>();

	// Separate off any CSS atts that have imports, and HTML imports
	let numHtmlAtts = 0;
	let numCssImportAtts = 0;
	const [maybeParentAtts, resolvedAtts] = partition(atts, attPlus => {
		const { att } = attPlus;

		const basicType = identifyType(att);

		if (basicType === MediaType.Html) {
			// HTML, store
			++numHtmlAtts;

			const str =
				att.attType === 'string' ? (att as Att2).str : (att as Att1).buf.toString('binary');

			dataStrsOfImportAtts.set(attPlus, str);

			if (verbose) {
				console.log(`Detected [${att.contentLocation}] as HTML`);
			}

			return true;
		}

		if (basicType === MediaType.Css) {
			// Css
			const str =
				att.attType === 'string' ? (att as Att2).str : (att as Att1).buf.toString('binary');

			const hasImport = /@import|url *\(/i.test(str);

			if (hasImport) {
				// CSS with import
				++numCssImportAtts;
				dataStrsOfImportAtts.set(attPlus, str);

				if (verbose) {
					console.log(`Detected [${att.contentLocation}] as CSS with @import`);
				}

				return true;
			} else {
				return false;
			}
		}

		return false;
	});

	if (verbose) {
		console.info(`inlineIntoCssAndHtmlAtts: got ${maybeParentAtts.length} maybe parent atts`);
		console.info(
			`inlineIntoCssAndHtmlAtts: got ${numHtmlAtts} HTML atts, ${numCssImportAtts} CSS import atts, and ${resolvedAtts.length} other atts`
		);
	}

	const stillUnresolvedMaybeParentAttSet = new Set(maybeParentAtts);
	const resolvedAttSet = new Set(resolvedAtts);
	const inlinedAtts = new Set<AttWithLocs<T>>();
	let resolveRounds = 0;

	let somethingChangedLastIteration;
	do {
		++resolveRounds;
		if (verbose) {
			console.log('\n\n--ATT RESOLVE ROUND ' + resolveRounds);
		}

		somethingChangedLastIteration = false;

		// Start off the iteration by collecting all of the still-unresolved stuff
		const currentListOfUnresolvedMaybeParentAtts = [...stillUnresolvedMaybeParentAttSet];

		for (const currentMaybeParentAtt of currentListOfUnresolvedMaybeParentAtts) {
			if (verbose) {
				console.log(`Working on parent [${currentMaybeParentAtt.att.contentLocation}]`);
			}

			const body = dataStrsOfImportAtts.get(currentMaybeParentAtt);

			if (!isDef(body)) {
				throw new Error('No body for maybe-parent att');
			}

			const otherAtts = atts.filter(o => o !== currentMaybeParentAtt);

			let matchedToUnresolvedAtt = false;
			let inlinedSomething = false;

			let body2: string;

			if (identifyType(currentMaybeParentAtt.att) === MediaType.Html) {
				// Handle HTML
				const result = doHtmlUrlInline(
					body,
					otherAtts,
					currentMaybeParentAtt.locs.lower?.str,
					rootContLocLower,
					// Callback to handle knowing what has been inlined
					matchingAtts => {
						// Check if this CSS import resolved to an attachment that itself has not been resolved yet
						for (const matchedAtt of matchingAtts) {
							if (stillUnresolvedMaybeParentAttSet.has(matchedAtt)) {
								if (verbose) {
									console.log(
										`Found att [${matchedAtt.att.contentLocation}] referenced in parent [${currentMaybeParentAtt.att.contentLocation}], but att is still unresolved`
									);
								}
								matchedToUnresolvedAtt = true;
								return false;
							}
						}

						// This import matched to a resolved att!
						inlinedSomething = true;

						for (const att of matchingAtts) {
							inlinedAtts.add(att);
						}
					},
					// Callback to get potential updated attachment data string
					att => updatedAttData.get(att),
					encodeString
				);

				body2 = result.html;
			} else {
				// Handle CSS @import
				const rxImport = new RegExp(rxsCssImport, 'gi');

				const cache = new Map<string, string>();

				body2 = body.replace(
					rxImport,
					(
						match,
						url1: string | undefined,
						url2: string | undefined,
						url3: string | undefined
					) => {
						const urlMatch = trimQuotePair(url1 ?? url2 ?? url3 ?? '');

						const cacheHit = cache.get(urlMatch);
						if (isDef(cacheHit)) {
							return cacheHit;
						}

						const result = ((): string => {
							const matchingAtts = matchUrlToAtt(
								urlMatch,
								otherAtts,
								currentMaybeParentAtt.locs.lower?.str,
								rootContLocLower
							);

							// If this import didn't match to anything, just return url as-is
							if (!matchingAtts.length) {
								return match;
							}

							// Check if this CSS import resolved to an attachment that itself has not been resolved yet
							for (const matchedAtt of matchingAtts) {
								if (stillUnresolvedMaybeParentAttSet.has(matchedAtt)) {
									if (verbose) {
										console.log(
											`Found att [${matchedAtt.att.contentLocation}] referenced in parent [${currentMaybeParentAtt.att.contentLocation}], but att is still unresolved`
										);
									}
									matchedToUnresolvedAtt = true;
									return match;
								}
							}

							// This import matched to a resolved att!
							inlinedSomething = true;

							for (const att of matchingAtts) {
								inlinedAtts.add(att);
							}

							const first = matchingAtts[0];
							const updatedAttStr = updatedAttData.get(first);
							const buf = getUpdatedAttBuf(first.att, updatedAttStr, encodeString);

							const dataUrl = `data:${first.att.contentType};base64,${buf.toString(
								'base64'
							)}`;

							return url3 ? `url('${dataUrl}')` : `@import url('${dataUrl}')`;
						})();

						cache.set(urlMatch, result);
						return result;
					}
				);
			}

			if (matchedToUnresolvedAtt) {
				// Can't do anything with this att at the moment...
			} else {
				// We fully resolved this HTML or CSS attachment, either by inlining other stuff, or no inlining was needed
				if (verbose) {
					console.log(`Resolved [${currentMaybeParentAtt.att.contentLocation}]`);
				}
				stillUnresolvedMaybeParentAttSet.delete(currentMaybeParentAtt);
				resolvedAttSet.add(currentMaybeParentAtt);

				if (inlinedSomething) {
					updatedAttData.set(currentMaybeParentAtt, body2);
				}

				somethingChangedLastIteration = true;
			}
		}
	} while (somethingChangedLastIteration && stillUnresolvedMaybeParentAttSet.size > 0);

	// At this point, we've inlined everything we can
	return {
		inlinedAtts: inlinedAtts,
		updatedAttData: updatedAttData,
	};
}

export function inlineAtts<T extends Att>(
	html: string,
	atts: Iterable<T>,
	rootHtmlContLoc?: string,
	encodeString?: StringEncoder,
	verbose = false
): InlineResult<T> {
	const atts1 = [...atts];
	if (!atts1.length) {
		return {
			html: html,
			notInlined: new Set<T>(),
		};
	}

	// Decode the content locations up front, to avoid doing on demand for each encountered URL in the HTML
	const atts2: AttWithLocs<T>[] = [...atts].map(att => {
		const lower = att.contentLocation?.toLowerCase();
		const parsed = lower ? maybeNewURL(lower) : undefined;
		const decoded = lower ? decodeURI(lower) : undefined;
		const decodedParsed = decoded ? maybeNewURL(decoded) : undefined;

		return {
			att: att,
			locs: {
				lower: {
					str: lower,
					parsed: parsed,
				},
				decoded: {
					str: decoded,
					parsed: decodedParsed,
				},
			},
		};
	});

	// Remove any query string or fragment from html content location
	rootHtmlContLoc = getUrlNoQsOrFragment(rootHtmlContLoc);
	const rootHtmlContLocLower = rootHtmlContLoc?.toLowerCase();

	const cssInliningResult = inlineIntoCssAndHtmlAtts(
		atts2,
		rootHtmlContLocLower,
		encodeString,
		verbose
	);
	if (verbose) {
		console.info(
			`Inlined ${cssInliningResult.inlinedAtts.size} attachments into ${cssInliningResult.updatedAttData.size} CSS attachments`
		);
	}

	const notInlined: Set<AttWithLocs<T>> = difference(
		new Set(atts2),
		cssInliningResult.inlinedAtts
	);

	const result = doHtmlUrlInline(
		html,
		atts2,
		rootHtmlContLocLower,
		rootHtmlContLocLower,
		// Callback to handle knowing what has been inlined
		matchingAtts => {
			// We got a match!
			for (const att of matchingAtts) {
				notInlined.delete(att);
			}
		},
		// Callback to get potential updated attachment data string
		att => cssInliningResult.updatedAttData.get(att),
		encodeString
	);

	return {
		html: result.html,
		notInlined: new Set([...notInlined].map(o => o.att)),
	};
}
